import { ApiError } from '@/lib/api';
import {
  createCart,
  addCartItems,
  createOrder,
  createCheckout,
  confirmStubPayment,
  getOrder as taquillaGetOrder,
  getInventory as taquillaGetInventory,
  getLocalities as taquillaGetLocalities,
  listMyOrders,
  type OrderDto,
} from '@/lib/api/taquilla';
import type { CartLine } from '@/store/cart';

/**
 * checkoutClient — fachada del flujo de checkout de la Taquillera contra
 * **api-taquilla** (FastAPI).
 *
 * Migración First Class: este módulo conserva su superficie pública previa
 * (tipos + funciones + `CheckoutClientError`) para minimizar el diff en los
 * consumidores (Checkout, CartReview, useInventory, useOrderPolling,
 * useMyOrders, MyTickets), pero por dentro delega en el cliente API tipado
 * (`@/lib/api/taquilla`) en vez de las edge functions de Supabase.
 *
 * Mapeo de flujo (legacy edge → taquilla FastAPI):
 *   - `lockCart`        → POST /v1/cart + /v1/cart/:id/items + /v1/orders
 *   - `initiatePayment` → POST /v1/payments/checkout (gateway=stub)
 *   - `submitStubWebhook` → ver GAP-01 (no hay confirmación stub
 *     client-callable; se hace best-effort + polling).
 *   - `getOrder` / `myOrders` → GET /v1/orders/:id | /v1/orders/mine
 *   - `getInventory`    → GET /v1/events/:id/inventory
 *
 * El JWT lo adjunta el cliente HTTP; la idempotencia viaja en header
 * `Idempotency-Key`.
 */

// ---- Types (superficie pública preservada) --------------------------------

export type LockCartRequest = {
  cartId: string;
  items: CartLine[];
  idempotencyKey: string;
};

export type LockCartResponse = {
  orderId: string;
  expiresAt: string; // ISO-8601
  lockedSeats: Array<{ stageId: string; seatIdentifier: string | null; quantity: number }>;
};

export type InitiatePaymentRequest = {
  orderId: string;
  providerSlug: string;
  idempotencyKey: string;
};

export type InitiatePaymentResponse = {
  paymentId: string;
  externalReference: string;
  flow: 'render_form_inline' | 'redirect' | 'open_widget' | 'stub_immediate';
  redirectUrl?: string;
};

export type SubmitStubWebhookRequest = {
  paymentId: string;
  externalReference: string;
  payload: Record<string, unknown>;
};

export type SubmitStubWebhookResponse = {
  processed: boolean;
  orderId: string;
  resultingOrderStatus: string;
};

export type OrderSummary = {
  id: string;
  orderNumber: string;
  status: string;
  totalCents: number;
  currency: string;
  items: Array<{
    id: string;
    itemType: string;
    stageId: string | null;
    localityId: string | null;
    seatIdentifier: string | null;
    quantity: number;
    unitPriceCents: number;
  }>;
  qrCodes: Array<{
    id: string;
    orderItemId: string;
    code: string;
    expiresAt: string;
    // F5 — QR nominal por silla (opcionales: mocks/QRs de cupo no los traen)
    seatLabel?: string | null;
    holderName?: string | null;
  }>;
  createdAt: string;
  confirmedAt: string | null;
};

/**
 * Error del flujo de checkout. Se mantiene la clase por compatibilidad con
 * los consumidores (Checkout, CartReview). Se construye a partir de `ApiError`
 * del cliente HTTP, preservando `code` / `status` / `retryable`.
 */
export class CheckoutClientError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly retryable: boolean;
  constructor(code: string, message: string, status: number, retryable = false) {
    super(message);
    this.name = 'CheckoutClientError';
    this.code = code;
    this.status = status;
    this.retryable = retryable;
  }

  static from(error: unknown): CheckoutClientError {
    if (error instanceof CheckoutClientError) return error;
    if (error instanceof ApiError) {
      return new CheckoutClientError(error.code, error.message, error.status, error.retryable);
    }
    const message = error instanceof Error ? error.message : 'Error inesperado';
    return new CheckoutClientError('UNKNOWN', message, 0, false);
  }
}

// ---- Normalizadores -------------------------------------------------------

function normalizeOrder(o: OrderDto): OrderSummary {
  return {
    id: String(o.id ?? ''),
    orderNumber: String(o.order_number ?? o.id ?? '—'),
    status: String(o.status ?? 'created'),
    // Backend money llega en centavos; la UI (formatCOP) trata COP como entero
    // sin decimales. Convertimos a pesos enteros (÷100) igual que el inventario
    // (centsToWholeCop), para no mostrar montos ×100.
    totalCents: centsToWholeCop(o.total_cents) ?? 0,
    currency: String(o.currency ?? 'COP'),
    items: (o.items ?? []).map((i) => ({
      id: String(i.id ?? ''),
      itemType: String(i.item_type ?? 'general_admission'),
      stageId: (i.stage_id ?? null) as string | null,
      localityId: (i.locality_id ?? null) as string | null,
      seatIdentifier: null,
      quantity: Number(i.quantity ?? 1) || 1,
      unitPriceCents: centsToWholeCop(i.unit_price_cents) ?? 0,
    })),
    qrCodes: (o.qr_codes ?? []).map((q) => ({
      id: String(q.id ?? ''),
      orderItemId: String(q.order_item_id ?? ''),
      code: String(q.code ?? ''),
      expiresAt: String(q.expires_at ?? ''),
      seatLabel: (q.seat_label ?? null) as string | null,
      holderName: (q.holder_name ?? null) as string | null,
    })),
    createdAt: String(o.created_at ?? ''),
    confirmedAt: (o.confirmed_at ?? null) as string | null,
  };
}

// ---- Public surface -------------------------------------------------------

/**
 * lockCart — convierte la selección del carrito en una orden reservada.
 *
 * Flujo taquilla: crea un cart para el evento, agrega las líneas (agrupadas
 * por `stageId`), y crea la orden (status `cart_locked`, TTL server-side).
 * Devuelve `{ orderId, expiresAt, lockedSeats }`.
 *
 * Nota: el carrito de taquilla es por evento. `lockCart` opera sobre el
 * evento de las líneas (todas comparten `eventId` en el flujo "Solo boletas";
 * el multi-evento está cubierto por GAP-06).
 */
export async function lockCart(req: LockCartRequest): Promise<LockCartResponse> {
  try {
    const eventId = req.items.find((l) => l.eventId)?.eventId;
    if (!eventId) {
      throw new CheckoutClientError('INVALID_CART', 'El carrito no tiene evento.', 400);
    }
    const cart = await createCart(eventId);

    // Dos clases de línea:
    //  - GA / cupo (seatIdentifier == null): se agrupan por stage (como siempre,
    //    SIN locality_id → el backend mantiene el pricing por etapa).
    //  - Por silla (seatIdentifier != null, F5): se agrupan por (stage, locality)
    //    y mandan seat_ids + holder_names para emitir un QR nominal por asiento.
    const items: import('@/lib/api/taquilla').CartItemInput[] = [];

    const byStage = new Map<string, number>();
    const bySeatGroup = new Map<
      string,
      { stage_id: string; locality_id: string; seat_ids: string[]; holder_names: string[] }
    >();
    for (const line of req.items) {
      if (line.seatIdentifier && line.localityId) {
        const key = `${line.stageId}::${line.localityId}`;
        let g = bySeatGroup.get(key);
        if (!g) {
          g = {
            stage_id: line.stageId,
            locality_id: line.localityId,
            seat_ids: [],
            holder_names: [],
          };
          bySeatGroup.set(key, g);
        }
        g.seat_ids.push(line.seatIdentifier);
        g.holder_names.push((line.holderName ?? '').trim());
      } else {
        byStage.set(line.stageId, (byStage.get(line.stageId) ?? 0) + line.quantity);
      }
    }
    for (const [stage_id, quantity] of byStage) items.push({ stage_id, quantity });
    for (const g of bySeatGroup.values()) {
      items.push({
        stage_id: g.stage_id,
        quantity: g.seat_ids.length,
        locality_id: g.locality_id,
        seat_ids: g.seat_ids,
        holder_names: g.holder_names,
      });
    }
    await addCartItems(cart.id, items);

    const order = await createOrder(cart.id, req.idempotencyKey);
    return {
      orderId: order.id,
      expiresAt: cart.expires_at ?? new Date(Date.now() + 15 * 60_000).toISOString(),
      lockedSeats: req.items.map((l) => ({
        stageId: l.stageId,
        seatIdentifier: l.seatIdentifier,
        quantity: l.quantity,
      })),
    };
  } catch (e) {
    throw CheckoutClientError.from(e);
  }
}

/**
 * initiatePayment — crea el intento de pago (gateway `stub`/ePayco) para la
 * orden. Devuelve el `paymentId` + `redirectUrl` + flow.
 */
export async function initiatePayment(
  req: InitiatePaymentRequest,
): Promise<InitiatePaymentResponse> {
  try {
    const returnUrl = `${window.location.origin}/checkout/success?order_id=${encodeURIComponent(
      req.orderId,
    )}`;
    const result = await createCheckout(
      req.orderId,
      returnUrl,
      req.providerSlug,
      req.idempotencyKey,
    );
    const isStub = (result.gateway ?? req.providerSlug) === 'stub';
    return {
      paymentId: result.payment_id,
      externalReference: result.gateway_payment_id ?? result.payment_id,
      flow: isStub ? 'stub_immediate' : 'redirect',
      redirectUrl: result.redirect_url ?? undefined,
    };
  } catch (e) {
    throw CheckoutClientError.from(e);
  }
}

/**
 * submitStubWebhook — confirmación del pago stub.
 *
 * GAP-01: la API taquilla no expone una confirmación de pago llamable por el
 * cliente (`/payments/:id/confirm` es admin-only; los webhooks exigen firma).
 * Best-effort: consultamos la orden y devolvemos su estado real. La UI entra
 * en polling hasta que el backend confirme; no se fabrica un `confirmed`.
 */
export async function submitStubWebhook(
  req: SubmitStubWebhookRequest,
): Promise<SubmitStubWebhookResponse> {
  // GAP-01 (resuelto): la tiquetera ahora expone `POST /payments/:id/confirm-stub`
  // llamable por el comprador dueño de la orden (gateway stub / sandbox). El
  // StubAdapter codifica el `simulated_outcome` en el payload (último carácter
  // del Idempotency-Key: 9→falla, 0→3DS, resto→captura). Solo confirmamos
  // cuando el outcome simulado es 'captured'; los otros casos quedan pendientes
  // y el caller maneja el estado por polling (como antes).
  const outcome = (req.payload?.simulated_outcome as string | undefined) ?? 'captured';
  if (outcome === 'captured') {
    try {
      const res = await confirmStubPayment(req.paymentId);
      return {
        processed: true,
        orderId: String(res.order_id ?? ''),
        resultingOrderStatus: String(res.status ?? 'qr_issued'),
      };
    } catch (e) {
      throw CheckoutClientError.from(e);
    }
  }
  // failed / requires_action → no confirmamos; el polling refleja el estado real.
  return { processed: true, orderId: '', resultingOrderStatus: 'payment_pending' };
}

/** GET /v1/orders/:id — estado de la orden + items + QRs. */
export async function getOrder(orderId: string): Promise<OrderSummary> {
  try {
    const raw = await taquillaGetOrder(orderId);
    return normalizeOrder(raw);
  } catch (e) {
    throw CheckoutClientError.from(e);
  }
}

// ---- Inventario -----------------------------------------------------------

function centsToWholeCop(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? Math.round(n / 100) : null;
}

/** Pesos COP enteros (lo que el endpoint de localidades ya expone, sin /100). */
function wholeCop(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
}

/**
 * Precio "desde" derivado de las localidades vendibles (pesos enteros). El
 * endpoint de inventario suele devolver `price_*_cents` en 0 para eventos sin
 * pricing por etapa; las localidades sí traen el precio real. Tomamos el mínimo
 * positivo de cada banda (full / promo) como respaldo del SeatPicker.
 */
function localityFloorPrices(localities: Array<{ price_full?: unknown; price_promo?: unknown }>): {
  fullCop: number | null;
  promoCop: number | null;
} {
  let fullCop: number | null = null;
  let promoCop: number | null = null;
  for (const l of localities) {
    const full = wholeCop(l.price_full);
    const promo = wholeCop(l.price_promo);
    if (full !== null && full > 0 && (fullCop === null || full < fullCop)) fullCop = full;
    if (promo !== null && promo > 0 && (promoCop === null || promo < promoCop)) promoCop = promo;
  }
  return { fullCop, promoCop };
}

/**
 * getInventory — adapta el inventario de taquilla
 * (`GET /v1/events/:id/inventory` → lista de etapas) al `InventoryResponse`
 * que consume el SeatPicker. Cada etapa se modela como un stage con una
 * "localidad" única = la etapa misma (la compra es por `stage_id`).
 */
export async function getInventory(eventId: string): Promise<import('@/types').InventoryResponse> {
  try {
    // Inventario (cupo + precio por etapa) y localidades (precio real de
    // respaldo) en paralelo — sin waterfall. Si las localidades fallan,
    // degradamos al precio de etapa sin romper el inventario.
    const [rawStages, rawLocalities] = await Promise.all([
      taquillaGetInventory(eventId),
      taquillaGetLocalities(eventId).catch(() => []),
    ]);
    const floor = localityFloorPrices(rawLocalities);
    let latest = '';
    const stages = rawStages.map((s) => {
      const cap = Number.isFinite(Number(s.capacity_total)) ? Number(s.capacity_total) : 0;
      const avail = Number.isFinite(Number(s.capacity_available))
        ? Number(s.capacity_available)
        : 0;
      const sold = Math.max(0, cap - avail);
      const name = typeof s.label === 'string' && s.label.trim() ? s.label : 'Etapa';
      const stageId = String(s.stage_id ?? '');
      if (typeof s.updated_at === 'string' && s.updated_at > latest) latest = s.updated_at;
      // El precio de la etapa viene en centavos; cuando está en 0/ausente (eventos
      // sin pricing por etapa migrado) caemos al precio "desde" de las localidades
      // (ya en pesos enteros) para que el SeatPicker nunca muestre COP 0.
      const stageFull = centsToWholeCop(s.price_full_cents);
      const stagePromo = centsToWholeCop(s.price_promo_cents);
      const priceFullCents = stageFull && stageFull > 0 ? stageFull : floor.fullCop;
      const pricePromoCents = stagePromo && stagePromo > 0 ? stagePromo : floor.promoCop;
      return {
        stageId,
        stageName: name,
        capacity: cap,
        sold,
        reserved: 0,
        available: avail,
        soldPct: cap > 0 ? Math.round((sold / cap) * 1000) / 10 : 0,
        localities: [
          {
            localityId: stageId,
            name,
            capacity: cap,
            sold,
            reserved: 0,
            available: avail,
            priceFullCents,
            pricePromoCents,
          },
        ],
      };
    });
    return {
      eventId,
      stages,
      snapshotAt: latest || new Date().toISOString(),
    };
  } catch (e) {
    throw CheckoutClientError.from(e);
  }
}

/** GET /v1/orders/mine — órdenes del usuario autenticado. */
export async function myOrders(): Promise<OrderSummary[]> {
  try {
    const raw = await listMyOrders();
    return raw.map(normalizeOrder);
  } catch (e) {
    throw CheckoutClientError.from(e);
  }
}
